"use client"

import { useCallback, useEffect, useState } from 'react'

import { fetchAuthChallengeStatus } from '@/api/auth'
import {
  type CaptchaConfig,
  type ChallengeStatus,
  normalizeChallengeStatus,
} from '@/lib/auth-captcha'

export function useAuthCaptcha() {
  const [status, setStatus] = useState<ChallengeStatus>({
    captchaRequired: false,
    recaptcha: { enabled: false, siteKey: null, version: 'v2' },
  })
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)

  const refreshStatus = useCallback(async () => {
    try {
      const data = await fetchAuthChallengeStatus()
      setStatus(normalizeChallengeStatus(data))
    } catch {
      /* koru */
    }
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => void refreshStatus(), 0)
    return () => window.clearTimeout(timer)
  }, [refreshStatus])

  const markCaptchaRequired = useCallback((recaptcha?: CaptchaConfig | null) => {
    setStatus((prev) => ({
      captchaRequired: true,
      recaptcha: recaptcha?.siteKey
        ? {
            enabled: Boolean(recaptcha.enabled ?? true),
            siteKey: recaptcha.siteKey,
            version: recaptcha.version ?? prev.recaptcha.version,
          }
        : prev.recaptcha,
    }))
  }, [])

  const clearCaptchaToken = useCallback(() => setRecaptchaToken(null), [])

  const captchaRequired = status.captchaRequired
  const showCaptcha = captchaRequired && Boolean(status.recaptcha.siteKey)

  return {
    captchaRequired,
    showCaptcha,
    siteKey: status.recaptcha.siteKey,
    recaptchaToken,
    setRecaptchaToken,
    refreshStatus,
    markCaptchaRequired,
    clearCaptchaToken,
  }
}
